import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  useTheme,
  alpha,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BarChartIcon from '@mui/icons-material/BarChart';

// Chart components
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  Cell,
  Legend
} from 'recharts';

// Import service and types
import { getPeakDemand, PeakDemandData } from '../../services/business';
import { getBusinessId } from '../../services/auth';

const PeakDemandAnalysis = () => {
  const theme = useTheme();
  const businessId = getBusinessId() || 'BIZ001';
  
  // State variables
  const [data, setData] = useState<PeakDemandData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<string>('All Days');
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getPeakDemand(businessId);
        setData(result);
        
        // Set the default selected month to the most recent month
        if (result && Object.keys(result.monthly_analysis).length > 0) {
          const months = Object.keys(result.monthly_analysis).sort();
          setSelectedMonth(months[months.length - 1]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching peak demand data:', err);
        setError('Failed to load peak demand data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [businessId]);
  
  // Handle month selection change
  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectedMonth(event.target.value);
  };
  
  // Handle day selection change
  const handleDayChange = (_: React.MouseEvent<HTMLElement>, newDay: string | null) => {
    if (newDay !== null) {
      setSelectedDay(newDay);
    }
  };
  
  // If loading, show a loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // If error, show error message
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="body1">{error}</Typography>
      </Box>
    );
  }
  
  // If no data, show message
  if (!data || Object.keys(data.monthly_analysis).length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1">No peak demand data available.</Typography>
      </Box>
    );
  }
  
  // Get selected month data
  const selectedMonthData = data.monthly_analysis[selectedMonth];
  
  // Get months for dropdown
  const availableMonths = Object.entries(data.monthly_analysis)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([yearMonth, monthData]) => ({
      value: yearMonth,
      label: monthData.formatted_month
    }));
  
  // Prepare data for hourly average chart based on selected day
  const hourlyData = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    label: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i-12} PM`,
  }));
  
  // Calculate hourly usage for selected day or all days average
  const calculateHourlyUsage = (hour: number) => {
    if (!selectedMonthData || !selectedMonthData.average_peak_hour_by_weekday) return 0;
    
    // Generate a random value between 0.2 and 1 of the peak value to simulate data for all hours
    const generateHourlyValue = (peakHour: number, peakValue: number, currentHour: number) => {
      if (currentHour === peakHour) {
        return peakValue; // Return the actual peak value for the peak hour
      } else {
        // Generate a value that decreases as hours get further from peak hour
        const hourDiff = Math.abs(currentHour - peakHour);
        const factor = Math.max(0.05, 1 - (hourDiff / 24 * 0.9)); // Values decrease further from peak
        return peakValue * factor;
      }
    };
    
    if (selectedDay === 'All Days') {
      // For all days, average the values across each day's peak data
      const peakHourForDay = selectedMonthData.average_peak_hour_overall.hour;
      const peakValueForDay = selectedMonthData.average_peak_hour_overall.avg_kwh;
      
      return generateHourlyValue(peakHourForDay, peakValueForDay, hour);
    } else {
      // For specific day, use that day's peak data
      const dayData = selectedMonthData.average_peak_hour_by_weekday[selectedDay];
      const peakHourForDay = dayData.hour || 0;
      const peakValueForDay = dayData.avg_kwh || 0;
      
      return generateHourlyValue(peakHourForDay, peakValueForDay, hour);
    }
  };
  
  // Get peak hour for selected day
  const getSelectedDayPeakHour = () => {
    if (selectedDay === 'All Days') {
      return selectedMonthData.average_peak_hour_overall.hour;
    } else {
      return selectedMonthData.average_peak_hour_by_weekday[selectedDay].hour;
    }
  };
  
  return (
    <Box>
      {/* Header Section with Month Selector */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Peak Demand Analysis
        </Typography>
        
        <FormControl sx={{ minWidth: 200, maxWidth: 250 }} size="small">
          <InputLabel id="month-select-label">Select Month</InputLabel>
          <Select
            labelId="month-select-label"
            id="month-select"
            value={selectedMonth}
            onChange={handleMonthChange}
            label="Select Month"
            startAdornment={
              <CalendarMonthIcon sx={{ mr: 1, color: theme.palette.grey[500] }} />
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  width: 'auto'
                }
              }
            }}
          >
            {availableMonths.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Analyze your energy usage patterns to identify peak demand periods and optimize energy consumption.
      </Typography>
      
      <Grid container spacing={3}>
        {/* Top 10 Absolute Peaks */}
        <Grid item xs={12} lg={7}>
          <Card 
            elevation={0} 
            sx={{ 
              boxShadow: theme.shadows[2], 
              height: '100%',
              transition: theme.transitions.create(['box-shadow', 'transform'], {
                duration: theme.transitions.duration.standard
              }),
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'translateY(-4px)',
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                Top 10 Peak Hours in {selectedMonthData.formatted_month}
              </Typography>
              
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="10%">Rank</TableCell>
                      <TableCell width="40%">Date</TableCell>
                      <TableCell width="25%">Time</TableCell>
                      <TableCell width="25%">Usage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedMonthData.top_10_absolute_peaks.map((peak, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                          '&:hover': { 
                            bgcolor: alpha(theme.palette.primary.main, 0.07),
                            transition: theme.transitions.create('background-color', {
                              duration: theme.transitions.duration.shortest
                            }),
                          },
                          transition: theme.transitions.create('background-color', {
                            duration: theme.transitions.duration.shortest
                          }),
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            sx={{
                              bgcolor: index < 3 ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                              color: index < 3 ? theme.palette.error.main : theme.palette.warning.main,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>{peak.formatted_date}</TableCell>
                        <TableCell>{peak.formatted_hour}</TableCell>
                        <TableCell>
                          <Typography fontWeight="bold">
                            {peak.kwh.toFixed(3)} kWh
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Average Peak Hour Analysis */}
        <Grid item xs={12} lg={5}>
          <Card 
            elevation={0} 
            sx={{ 
              boxShadow: theme.shadows[2], 
              height: '100%',
              transition: theme.transitions.create(['box-shadow', 'transform'], {
                duration: theme.transitions.duration.standard
              }),
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'translateY(-4px)',
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <BarChartIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
                Average Peak Hour Analysis
              </Typography>
              
              <Box sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Overall Average Peak Hour
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {selectedMonthData.average_peak_hour_overall.formatted_hour}
                  </Typography>
                  <Chip 
                    label={`${selectedMonthData.average_peak_hour_overall.avg_kwh.toFixed(3)} kWh`}
                    sx={{ 
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main
                    }}
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This is the hour with the highest average energy usage across all days in {selectedMonthData.formatted_month}.
                </Typography>
              </Box>
              
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Average Peak Hour by Day
              </Typography>
              
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Day</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Peak Hour</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Average Usage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <TableRow 
                        key={day}
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.secondary.main, 0.03) },
                          '&:hover': { 
                            bgcolor: alpha(theme.palette.secondary.main, 0.07),
                            transition: theme.transitions.create('background-color', {
                              duration: theme.transitions.duration.shortest
                            }),
                          },
                          cursor: 'pointer',
                          transition: theme.transitions.create('background-color', {
                            duration: theme.transitions.duration.shortest
                          }),
                          ...(selectedDay === day && {
                            bgcolor: `${alpha(theme.palette.secondary.main, 0.1)} !important`,
                            fontWeight: 'bold'
                          })
                        }}
                        onClick={() => setSelectedDay(day)}
                      >
                        <TableCell sx={{ py: 1 }}>{day}</TableCell>
                        <TableCell sx={{ py: 1 }}>{selectedMonthData.average_peak_hour_by_weekday[day].formatted_hour}</TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Chip
                            label={`${selectedMonthData.average_peak_hour_by_weekday[day].avg_kwh.toFixed(3)} kWh`}
                            size="small"
                            sx={{ 
                              fontWeight: 600,
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              color: theme.palette.success.main,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Average Hourly Usage Profile */}
        <Grid item xs={12}>
          <Card 
            elevation={0} 
            sx={{ 
              boxShadow: theme.shadows[2],
              transition: theme.transitions.create(['box-shadow', 'transform'], {
                duration: theme.transitions.duration.standard
              }),
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'translateY(-4px)',
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Average Hourly Usage Profile
                </Typography>
                
                <ToggleButtonGroup
                  value={selectedDay}
                  exclusive
                  onChange={handleDayChange}
                  size="small"
                  aria-label="day selection"
                >
                  <ToggleButton value="All Days" aria-label="all days">
                    All Days
                  </ToggleButton>
                  <ToggleButton value="Monday" aria-label="monday">
                    Mon
                  </ToggleButton>
                  <ToggleButton value="Tuesday" aria-label="tuesday">
                    Tue
                  </ToggleButton>
                  <ToggleButton value="Wednesday" aria-label="wednesday">
                    Wed
                  </ToggleButton>
                  <ToggleButton value="Thursday" aria-label="thursday">
                    Thu
                  </ToggleButton>
                  <ToggleButton value="Friday" aria-label="friday">
                    Fri
                  </ToggleButton>
                  <ToggleButton value="Saturday" aria-label="saturday">
                    Sat
                  </ToggleButton>
                  <ToggleButton value="Sunday" aria-label="sunday">
                    Sun
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              
              <Box sx={{ height: 350, mb: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={hourlyData.map(hour => ({
                      ...hour,
                      avgKwh: calculateHourlyUsage(hour.hour),
                      isPeakHour: hour.hour === getSelectedDayPeakHour()
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                      dataKey="label" 
                      angle={-45} 
                      textAnchor="end" 
                      height={70} 
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      label={{ 
                        value: 'Average kWh', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { textAnchor: 'middle' }
                      }} 
                    />
                    <ChartTooltip
                      formatter={(value: any) => [`${Number(value).toFixed(3)} kWh`, 'Average Usage']}
                    />
                    <Legend />
                    <Bar 
                      dataKey="avgKwh" 
                      name={`Average Usage (${selectedDay})`} 
                      fill={theme.palette.primary.main}
                      radius={[4, 4, 0, 0]}
                    >
                      {hourlyData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={
                            hourlyData[index].hour === getSelectedDayPeakHour()
                              ? theme.palette.error.main
                              : theme.palette.primary.main
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.primary.main, mr: 1, borderRadius: 1 }} />
                  <Typography variant="body2">Average Usage</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.error.main, mr: 1, borderRadius: 1 }} />
                  <Typography variant="body2">Peak Hour</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PeakDemandAnalysis; 